import { Button, Dropdown, Flex, Tag, Tooltip, Typography } from 'antd'
import type { TableColumnsType } from 'antd'
import { MoreHorizontal, Pencil, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { UserGroup } from 'src/api/main'
import {
  ContentSpinner,
  DateTimeCell,
  ProTableContainer,
  QueryErrorAlert,
  TableActionColumnTitle
} from 'src/components'
import {
  useHasWorkspacePermissions,
  useQueryParams,
  useTableSorter
} from 'src/hooks'
import { defaultFilters, WorkspacePermissionCode } from 'src/utils'

import { useApplicationNameMap, useWorkspaceUserGroups } from '../hooks'

import { GroupMembersCell } from './GroupMembersCell'
import { UserGroupDeleteModal } from './UserGroupDeleteModal'
import { UserGroupDetailDrawer } from './UserGroupDetailDrawer'
import { UserGroupFormDrawer } from './UserGroupFormDrawer'

const { Text } = Typography

const MAX_ROLE_TAGS = 3
const DEFAULT_ORDERING = '-date_created'

type Props = { workspaceId: number }

export const UserGroupsPanel = ({ workspaceId }: Props) => {
  const { t } = useTranslation()

  const params = useQueryParams()
  const sorter = useTableSorter()

  const [formGroup, setFormGroup] = useState<UserGroup | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const canManageGroups = useHasWorkspacePermissions(
    WorkspacePermissionCode.GROUPS_MANAGE
  )
  const [deleteGroup, setDeleteGroup] = useState<UserGroup | null>(null)
  const [detailGroup, setDetailGroup] = useState<UserGroup | null>(null)

  const { groups, total, isLoading, isFetching, isError, refetch } =
    useWorkspaceUserGroups({
      workspaceId,
      query: {
        ...defaultFilters,
        ...params,
        ordering: String(params.ordering ?? '') || DEFAULT_ORDERING
      }
    })

  const openCreate = () => {
    setFormGroup(null)
    setFormOpen(true)
  }

  const openEdit = (group: UserGroup) => {
    setFormGroup(group)
    setFormOpen(true)
  }

  // Role names only disambiguate inside their application, so every tag names
  // the scope it belongs to.
  const { appNameById } = useApplicationNameMap(workspaceId)
  const scopeLabel = (applicationId: number | null | undefined) =>
    applicationId == null
      ? t('users.groups.organizationScope')
      : (appNameById.get(applicationId) ?? `#${applicationId}`)

  const columns: TableColumnsType<UserGroup> = [
    {
      title: t('users.groups.columns.name'),
      key: 'name',
      fixed: 'left',
      dataIndex: 'name',
      width: 240,
      ellipsis: true,
      render: (name: string) => (
        <Text strong style={{ fontSize: 13 }} ellipsis={{ tooltip: name }}>
          {name}
        </Text>
      )
    },
    {
      title: t('users.groups.columns.members'),
      key: 'users',
      width: 190,
      render: (_, group) => <GroupMembersCell members={group.users ?? []} />
    },
    {
      title: t('users.groups.columns.roles'),
      key: 'roles',
      width: 230,
      render: (_, group) => {
        const roles = group.roles ?? []
        if (roles.length === 0) {
          return (
            <Text type="secondary" style={{ fontSize: 12 }}>
              —
            </Text>
          )
        }

        const shown = roles.slice(0, MAX_ROLE_TAGS)
        const overflow = roles.length - shown.length

        return (
          <Flex gap={4} wrap="wrap">
            {shown.map((role) => (
              <Tag key={role.id} style={{ marginInlineEnd: 0, fontSize: 11 }}>
                <Text type="secondary" style={{ fontSize: 10 }}>
                  {scopeLabel(role.application)}
                </Text>
                {` · ${role.name}`}
              </Tag>
            ))}
            {overflow > 0 ? (
              <Tooltip
                title={roles
                  .map((r) => `${scopeLabel(r.application)} · ${r.name}`)
                  .join(', ')}
              >
                <Tag style={{ marginInlineEnd: 0, fontSize: 11 }}>
                  +{overflow}
                </Tag>
              </Tooltip>
            ) : null}
          </Flex>
        )
      }
    },
    {
      title: t('users.groups.columns.created'),
      key: 'date_created',
      dataIndex: 'date_created',
      width: 130,
      render: (v: string) => (
        <DateTimeCell value={v} variant="date" style={{ fontSize: 12 }} />
      )
    },
    {
      title: t('users.groups.columns.updated'),
      key: 'date_updated',
      dataIndex: 'date_updated',
      width: 140,
      render: (v: string) => (
        <DateTimeCell value={v} variant="relative" style={{ fontSize: 12 }} />
      )
    },
    // Edit and delete are the only entries, so without `groups.manage` the
    // column is an empty menu button.
    ...(canManageGroups
      ? ([
          {
            title: <TableActionColumnTitle />,
            key: 'actions',
            width: 56,
            align: 'center' as const,
            fixed: 'right' as const,
            // The row opens the detail drawer, so the menu must not bubble into it.
            onCell: () => ({ onClick: (e) => e.stopPropagation() }),
            render: (_, group) => (
              <Dropdown
                trigger={['click']}
                menu={{
                  items: [
                    {
                      key: 'edit',
                      icon: <Pencil size={14} />,
                      label: t('users.groups.actions.edit'),
                      onClick: () => openEdit(group)
                    },
                    {
                      key: 'delete',
                      icon: <Trash2 size={14} />,
                      label: t('users.groups.actions.delete'),
                      danger: true,
                      onClick: () => setDeleteGroup(group)
                    }
                  ]
                }}
              >
                <Button
                  type="text"
                  size="small"
                  icon={<MoreHorizontal size={14} />}
                  aria-label={t('users.groups.actions.more')}
                />
              </Dropdown>
            )
          }
        ] satisfies TableColumnsType<UserGroup>)
      : [])
  ]

  if (isLoading) return <ContentSpinner minHeight={400} />
  if (isError) return <QueryErrorAlert onRetry={() => void refetch()} />

  return (
    <Flex vertical gap={14}>
      <ProTableContainer<UserGroup>
        tableIdentifier="user-groups"
        searchPlaceholder={t('users.groups.searchPlaceholder')}
        loading={isFetching}
        columns={columns}
        data={{ count: total, results: groups }}
        rowKey={(group) => group.id}
        onRow={(group) => ({
          onClick: () => setDetailGroup(group)
        })}
        onChange={(_, __, result) => sorter(result)}
        showSorterTooltip={false}
        scroll={{ x: 900 }}
        tableLayout="fixed"
        rightChildren={
          !canManageGroups ? null : (
            <Button
              type="primary"
              size="small"
              icon={<Plus size={14} />}
              onClick={openCreate}
            >
              {t('users.groups.actions.create')}
            </Button>
          )
        }
      />

      <UserGroupDetailDrawer
        workspaceId={workspaceId}
        group={detailGroup}
        canManage={canManageGroups}
        onClose={() => setDetailGroup(null)}
        onEdit={(group) => {
          setDetailGroup(null)
          openEdit(group)
        }}
        onDelete={(group) => {
          setDetailGroup(null)
          setDeleteGroup(group)
        }}
      />

      <UserGroupFormDrawer
        workspaceId={workspaceId}
        open={formOpen}
        group={formGroup}
        onClose={() => setFormOpen(false)}
      />

      <UserGroupDeleteModal
        workspaceId={workspaceId}
        open={deleteGroup != null}
        group={deleteGroup}
        onClose={() => setDeleteGroup(null)}
      />
    </Flex>
  )
}
